import React, { useEffect, useRef } from 'react';
import {
  ConfirmationTypes,
  ToUserTypings,
  TypeOfUser,
  cancelBooking,
  getTypeOfUser,
  pharmacistActions,
  pharmacyActions,
  pharmacyCancelBooking,
  selfPharmacistCancelBookings,
  selfPharmacyCancelBooking,
  successOrSelector,
} from '@pharmaplan/common';
import { FormikValues } from 'formik';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import strings from '../localization';
import { resetDialog, setDialog } from '../reducers/dialogReducer';
import ConfirmationModal from '../components/Modals/ConfirmationModal';
import OutcomeModal from '../components/Modals/OutcomeModal/OutcomeModal';
import { DeactivateModalIcon, OutcomeModalTypes } from '../helpers/Constants';
import DeactivateDialog from '../components/Admin/PharmacistList/DeactivateDialog';
import MultiBookingActionScreen from '../components/MultiBookingActionScreen';
import { MultiBookingActionType } from './MultiBookingActionScreen/useMultiBookingActions';
import useCancelMultipleBooking from './useCancelMultipleBooking';

export type IUserCancellationParams = {
  professionalId?: string;
  longTermShiftContractId?: string;
  bookingId: string;
  onSubmit?: (values: FormikValues) => void;
  isAutoSyncRecord?: boolean;
};

const useCancellationBooking = () => {
  const dispatch = useAppDispatch();
  const userType = useAppSelector(getTypeOfUser);
  const { showCancellationDialog } = useCancelMultipleBooking();
  const params = useRef<{ id: string; isAutoSyncRecord?: boolean }>({
    id: '',
    isAutoSyncRecord: false,
  });

  const isPharmacist = userType === TypeOfUser.pharmacist;

  const successes = useAppSelector((state) =>
    successOrSelector(
      [pharmacyActions.cancelBookings, pharmacistActions.selfCancelBooking],
      state,
    ));

  const pharmacistCancel = () => {
    dispatch(
      selfPharmacistCancelBookings({
        id: params.current?.id,
        isAutoSyncRecord: params.current?.isAutoSyncRecord,
      }),
    );
  };

  const pharmacyCancel = () => {
    dispatch(selfPharmacyCancelBooking(params.current?.id));
  };

  const cancelConfig = ToUserTypings({
    [TypeOfUser.technician]: pharmacistCancel,
    [TypeOfUser.student]: pharmacistCancel,
    [TypeOfUser.pharmacist]: pharmacistCancel,
    [TypeOfUser.pharmacy]: pharmacyCancel,
  });

  const successModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <OutcomeModal
            type={OutcomeModalTypes.selfServiceCancelConfirmation}
            message={strings.bookingCanceled}
            showCloseButton
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  useEffect(() => {
    if (successes) {
      dispatch(resetDialog());
      dispatch(successModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successes]);

  const handleSelfCancellation = (bookingId: string, isAutoSyncRecord?: boolean) => {
    params.current = { id: bookingId, isAutoSyncRecord };
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.cancelTAndCModal}
            confirmAction={cancelConfig[userType]}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const handleCancelConfirmation = (values: FormikValues) => {
    const { id, reason } = values ?? {};

    if (isPharmacist) {
      dispatch(cancelBooking({ bookingid: id, reason }));
    } else {
      dispatch(pharmacyCancelBooking({ bookingid: id, reason }));
    }
  };

  const showCancelConfirmation = (
    id: string,
    onSubmit: (values: FormikValues) => void,
  ) => {
    dispatch(
      setDialog({
        showCloseButton: true,
        Component: (
          <DeactivateDialog
            id={id}
            heading={strings.cancelBookingConfirmation}
            confirmationMessage={strings.confirmation}
            iconType={DeactivateModalIcon.cancelBooking}
            onSubmit={onSubmit}
          />
        ),
        heading: {
          title: '',
        },
      }),
    );
  };

  const handleSelfCancel = ({
    bookingId,
    longTermShiftContractId,
    professionalId,
    isAutoSyncRecord,
  }: IUserCancellationParams) =>
    () => {
      if (longTermShiftContractId) {
        dispatch(
          setDialog({
            Component: (
              <MultiBookingActionScreen
                type={MultiBookingActionType.CancelBooking}
                longTermContractId={longTermShiftContractId}
                professionalId={professionalId}
              />
            ),
            heading: {
              title: '',
            },
            maxWidth: 'lg',
            showCloseButton: true,
          }),
        );
      } else {
        handleSelfCancellation(bookingId, isAutoSyncRecord);
      }
    };

  const handleFullCancel = ({
    bookingId,
    longTermShiftContractId,
    professionalId,
    onSubmit,
  }: IUserCancellationParams) =>
    () => {
      console.log('here');
      if (longTermShiftContractId) {
        showCancellationDialog({ professionalId, longTermShiftContractId });
      } else {
        onSubmit && showCancelConfirmation(bookingId, onSubmit);
      }
    };

  return {
    handleSelfCancellation: handleSelfCancel,
    showCancelConfirmation: handleFullCancel,
    handleCancelConfirmation,
  };
};

export default useCancellationBooking;
